<script setup>
import FormGroup from '@maison/components/form/FormGroup.vue';
import { PhxStrapFinderPicture } from '@phx-styleguides/iwc';
import {
  PhxsgAccordion,
  PhxsgAccordionContent,
  PhxsgAccordionItem,
  PhxsgAccordionTrigger,
} from '@sg/src/View/Components/Molecules/Accordion';
import { computed, inject, ref } from 'vue';
import { emits as defaultEmits, props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import CheckboxListField from '@maison/components/form/fields/CheckboxListField.vue';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';

const props = defineProps({
  ...defaultProps,
  currentBuckleType: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(defaultEmits);

const { id, preTitle, nextSectionId } = useSection('strap-style');

const currentStrapStyles = defineModel('currentStrapStyles', {
  type: Array,
  required: true,
});

const straps = inject('straps', []);

const accordionValue = ref(null);

const strapStyleField = computed(() => {
  // We need the buckle type to filter the straps.
  if (!props.currentBuckleType) return null;

  // We filter the straps by buckle type and then group them by strap type. We assume the label and code are unique for each strap type.
  const groupedStrapsByType = straps.value
    .filter(({ buckleType, isSteelStrap }) => isSteelStrap || buckleType.code === props.currentBuckleType)
    .reduce((acc, { type, ...strap }) => {
      const { code, label } = type;
      acc[code] = acc[code] || { label, straps: [] };
      acc[code].straps.push(strap);
      return acc;
    }, {});

  return {
    id: id.value,
    name: id.value,
    label: '',
    types: Object.entries(groupedStrapsByType).map(([code, { label, straps }]) => ({
      id: code,
      value: code,
      label,
      colors: Object.values(
        straps.reduce((acc, { color, image }) => {
          // Here we'll create all the color options for each strap type. We'll use the strap style code and color code to create a unique id for each option.
          const colorCode = `${code}.${color.code}`;
          acc[colorCode] = acc[colorCode] || {
            id: colorCode,
            value: colorCode,
            label: color.label,
            media: {
              id: colorCode,
              captionText: label, // we display the strap type label as the caption text
              ...image,
            },
            color: getColorFromCode(color.code),
          };
          return acc;
        }, {}),
      ),
    })),
  };
});

const allColors = computed(() => {
  return strapStyleField.value?.types.flatMap((type) => type.colors.map((color) => color)) || [];
});

function onOptionClick(event, id, changeMedia) {
  if (!currentStrapStyles.value.length) {
    // if we don't have any strap styles selected, we emit an event to validate the previous step so this
    // section has to be validated again
    emit('onStepValidate', null, 'buckle-type');
    return;
  }

  emit('onStepValidate', event, id, changeMedia);
}

function getColorFromCode(code) {
  // This function maps color codes to actual colors. You can expand this mapping as needed.
  const colors = {
    STEEL: '#4682B4',
    BLACK: '#000000',
    BROWN: '#A52A2A',
    BLEU_AZUR: '#007FFF',
    GREEN: '#008000',
    BEIGE: '#F5F5DC',
    YELLOW: '#FFFF00',
    WHITE: '#FFFFFF',
    RED: '#FF0000',
    PINK: '#FFC0CB',
    ORANGE: '#FFA500',
    BRONZE: '#CD7F32',
    LIGHT_GREY: '#D3D3D3',
    GREY: '#808080',
    LIGHT_BLUE: '#ADD8E6',
    COGNAC_COLOURED: '#9A463D',
    BROWN_II: '#8B4513',
    BLUE_II: '#0000CD',
    LIGHT_BROWN: '#A0522D',
    BLACK_GRAY: '#2F2F2F',
    BURGUNDY: '#800020',
    PURPLE: '#800080',
    DARK_BLUE: '#00008B',
    LIGHT_GREEN: '#90EE90',
    LILAC: '#C8A2C8',
  };
  return colors[code] || code;
}
</script>

<template>
  <configurator-section-layout
    :id="id"
    :title="title"
    :description="description"
    :memo="memo"
    :pre-title="preTitle"
    :next-section-id="nextSectionId"
  >
    <template #media>
      <phx-strap-finder-picture v-for="color in allColors" :key="color.id" v-bind="color.media" />
    </template>

    <template #default="{ onShowMedia }">
      <form @submit.prevent.stop @change="onOptionClick($event, id, onShowMedia)">
        <phxsg-accordion v-if="strapStyleField" ref="accordion" v-model="accordionValue" boxed type="multiple">
          <phxsg-accordion-item v-for="type in strapStyleField.types" :id="type.id" :key="type.id">
            <phxsg-accordion-trigger>
              <span>
                {{ type.label }}
              </span>
            </phxsg-accordion-trigger>

            <phxsg-accordion-content>
              <div class="box stack stack--spacing-6">
                <form-group
                  :attrs="{ id: type.id, name: type.id, options: type.colors }"
                  :is-fieldset="true"
                  :fieldset-horizontal="true"
                >
                  <checkbox-list-field v-model="currentStrapStyles" type="color" />
                </form-group>
              </div>
            </phxsg-accordion-content>
          </phxsg-accordion-item>
        </phxsg-accordion>
      </form>
    </template>
  </configurator-section-layout>
</template>
